/**
 * CodeMirror 6 wrapper for the formula editor. Replaces the old `<textarea>`-based editor inside
 * `FormulaBar.tsx` while keeping the exact same imperative behaviors the Grid relies on:
 *   - `data-formula-editor="1"` on the editable node, so a header click while the bar is focused
 *     inserts a reference (see `Grid.tsx`'s `insertRefFromHeader`).
 *   - `insertRequest` — a nonce-keyed request to insert `[Column]` at the current caret.
 *   - Controlled `value`, synced without fighting the user's own typing/selection.
 *
 * `singleLine` picks between the inline formula bar (Enter commits, no newline ever insertable)
 * and the multi-line "new column" panel (Enter inserts a newline, Ctrl/Cmd+Enter commits) — the
 * two are always separate mounts (Grid.tsx renders one `<FormulaBar expanded={false|true}>` or
 * the other, never both at once), so `singleLine` never changes on a live instance.
 */

import { useEffect, useRef } from 'react'
import { Annotation, Compartment, EditorState, Prec, type Extension } from '@codemirror/state'
import { EditorView, ViewPlugin, keymap, placeholder as placeholderExtension, type ViewUpdate } from '@codemirror/view'
import {
  acceptCompletion,
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  closeCompletion,
  completionKeymap,
  completionStatus,
  startCompletion,
} from '@codemirror/autocomplete'
import { bracketMatching } from '@codemirror/language'
import { defaultKeymap, history, historyKeymap, insertNewlineAndIndent } from '@codemirror/commands'
import type { FormulaLocale, FunctionMetadata } from '@/shared/formula/language'
import {
  formulaCompletionSource,
  funcBeingTyped,
  keywordSpotAt,
  refBeingTyped,
  type FormulaCompletionColumn,
  type FormulaCompletionSchema,
  type FormulaOperandRef,
} from './formulaCompletion'
import { formulaLanguage } from './formulaLanguage'
import { formulaSignatureHelp, type SignatureHelpLabels } from './formulaSignature'
import { formulaTheme } from './formulaTheme'
import { useI18n } from '@/shared/i18n/useT'

function languageExtensions(
  locale: FormulaLocale,
  functionMetadata: readonly FunctionMetadata[],
  helpLabels: SignatureHelpLabels,
  dockedHelp: boolean,
): Extension {
  return [formulaLanguage(locale), bracketMatching(), formulaSignatureHelp(locale, functionMetadata, helpLabels, dockedHelp)]
}

/**
 * Deletes the dot in `'Sheet'.` the moment a `[` is typed after it, in the SAME transaction, so
 * the text becomes `'Sheet'[` without a visible flicker or a second undo step.
 *
 * Anchored on the character immediately before the bracket, which is the only dot that can be
 * wrong: the one separating a document from a sheet sits further left, behind a name, and is
 * never reachable from here.
 */
/**
 * Catches text committed by an input method that the editor did not absorb on its own.
 *
 * The Windows emoji panel (Win + .) commits through the Text Services Framework, which reaches a
 * `contenteditable` as a composition rather than as ordinary typing. Most of the time CodeMirror
 * reads that back out of the DOM correctly; when it cannot, it re-renders from its own state
 * instead — which silently throws the emoji away, and looks to the user like the picker did
 * nothing at all.
 *
 * So: count document changes, and when a composition ends, check whether the count moved. If it
 * did not, the composition was dropped, and its committed text is inserted here instead.
 *
 * Counting changes rather than comparing the text is what makes this safe. Comparing content
 * looks equivalent and is not: a composition that replaces a selection with the same characters,
 * or that lands while an earlier edit is still being read out of the DOM, leaves the document
 * looking untouched and would be inserted a second time. The counter answers the question that
 * actually matters — did anything reach the document at all.
 *
 * This is what keeps ordinary IME use (Chinese, Japanese, dictation) unaffected: those compose
 * through the same events, CodeMirror takes them, the count moves, and this does nothing. The
 * check is deferred to a macrotask because CodeMirror finishes reading the DOM after its own
 * `compositionend` handler returns.
 */
const compositionFallback = ViewPlugin.fromClass(
  class {
    /** Every document change this editor has seen, counted. Only ever compared with itself. */
    private changes = 0
    /** The change count and caret as they were when the composition began. */
    private before: { changes: number; from: number; to: number } | null = null

    constructor(readonly view: EditorView) {}

    update(update: ViewUpdate): void {
      if (update.docChanged) this.changes++
    }

    start(): void {
      const { from, to } = this.view.state.selection.main
      this.before = { changes: this.changes, from, to }
    }

    end(text: string): void {
      const before = this.before
      this.before = null
      if (before === null || text === '') return
      const { view } = this
      setTimeout(() => {
        // Something landed: the editor handled it, and inserting again would double it.
        if (this.changes !== before.changes) return
        const max = view.state.doc.length
        const from = Math.min(before.from, max)
        const to = Math.min(before.to, max)
        view.dispatch({
          changes: { from, to, insert: text },
          selection: { anchor: from + text.length },
          userEvent: 'input.type',
        })
      })
    }
  },
  {
    eventHandlers: {
      compositionstart() { this.start() },
      compositionend(event: CompositionEvent) { this.end(event.data ?? '') },
    },
  },
)

export const stripDotBeforeBracket = EditorState.transactionFilter.of((transaction) => {
  if (!transaction.docChanged || transaction.isUserEvent('undo') || transaction.isUserEvent('redo')) {
    return transaction
  }
  let bracketAt: number | null = null
  transaction.changes.iterChanges((fromA, _toA, _fromB, _toB, inserted) => {
    // `closeBrackets()` inserts the pair `[]` as a single change, so this is a prefix test rather
    // than an equality one — matching only `[` would miss every real keystroke.
    if (inserted.toString().startsWith('[')) bracketAt = fromA
  })
  if (bracketAt === null) return transaction
  const before = transaction.startState.doc.sliceString(Math.max(0, bracketAt - 1), bracketAt)
  if (before !== '.') return transaction
  return [transaction, { changes: { from: bracketAt - 1, to: bracketAt } }]
})

function completionExtensions(
  schema: FormulaCompletionSchema,
  functionMetadata: readonly FunctionMetadata[],
  locale: FormulaLocale,
  operandRefs?: () => readonly FormulaOperandRef[],
): Extension {
  return [
    autocompletion({
      override: [formulaCompletionSource(schema, functionMetadata, locale, operandRefs)],
      activateOnTyping: true,
    }),
    // Excel/Sheets accept the highlighted suggestion with Tab, not just Enter. `acceptCompletion`
    // returns false (falls through) when there's nothing open to accept, so this never steals Tab
    // otherwise.
    keymap.of([{ key: 'Tab', run: acceptCompletion }, ...completionKeymap]),
    keywordListOnArrival(functionMetadata, locale),
  ]
}

/**
 * Typing opens the list on its own, but a caret that arrives at a keyword argument by a click, an
 * arrow key or a deletion does not type anything. This opens the list there too, once per arrival:
 * Escape closes it and it stays closed until the caret moves again.
 */
function keywordListOnArrival(functionMetadata: readonly FunctionMetadata[], locale: FormulaLocale): Extension {
  return ViewPlugin.fromClass(class {
    update(update: ViewUpdate): void {
      if (!update.selectionSet || !update.view.hasFocus || completionStatus(update.state) !== null) return
      const { main } = update.state.selection
      if (!main.empty) return
      if (keywordSpotAt(update.state.doc.toString(), main.head, locale, functionMetadata) === null) return
      // A view cannot dispatch from inside its own update.
      const view = update.view
      queueMicrotask(() => {
        if (completionStatus(view.state) === null) startCompletion(view)
      })
    }
  })
}

const pointAnnotation = Annotation.define<boolean>()
/** Marks a document replacement that came from the `value` prop, not from the user. */
const externalValueAnnotation = Annotation.define<boolean>()

export type FormulaEditorColumn = FormulaCompletionColumn

export interface FormulaEditorProps {
  value: string
  onChange: (text: string) => void
  onCommit: () => void
  onCancel: () => void
  singleLine?: boolean
  variant?: 'bar' | 'panel' | 'cell'
  columns: readonly FormulaEditorColumn[]
  /** Documents, sheets and columns the dropdown may offer. See `formulaCompletion.ts`. */
  schema: FormulaCompletionSchema
  locale: FormulaLocale
  /** Locale-aware `syntax`/`description` for autocomplete and signature help — see
   *  `formulaFunctionMetadata.ts`. Independent of `locale` (decimal/argument separator): this
   *  follows the UI language instead. */
  functionMetadata: readonly FunctionMetadata[]
  insertRequest?: { field: string; nonce: number } | undefined
  autoFocus: boolean
  syntaxError?: string | null
  placeholder?: string
  /** Fires whenever the caret sits inside an in-progress `[reference` or function name, so the
   *  parent can suppress the syntax-error message mid-keystroke (the same heuristic the old
   *  textarea editor applied locally). */
  onActiveTypingChange?: (active: boolean) => void
  onFocusChange?: (focused: boolean) => void
  /** Excel/Sheets "point mode": when the caret sits where an operand belongs (after `=`, `(`, an
   *  operator or a separator), arrow keys move a pointer over the sheet. The parent moves it —
   *  from the edited cell when `fresh`, else from where it last was — and returns the reference
   *  text to insert, or null to swallow the key. Repeated arrows replace the reference in place. */
  onPointRef?: ((deltaRow: number, deltaCol: number, fresh: boolean) => string | null) | undefined
  /** A cell was clicked: insert its reference at the caret, or replace the one just pointed. */
  pointRequest?: { ref: string; nonce: number } | undefined
  /** References suggested first after `=` or an operator. See `formulaCompletionSource`. */
  operandRefs?: readonly FormulaOperandRef[] | undefined
}

export default function FormulaEditor({
  value,
  onChange,
  onCommit,
  onCancel,
  singleLine = true,
  variant = singleLine ? 'bar' : 'panel',
  columns,
  schema,
  locale,
  functionMetadata,
  insertRequest,
  autoFocus,
  syntaxError = null,
  placeholder = '',
  onActiveTypingChange,
  onFocusChange,
  onPointRef,
  pointRequest,
  operandRefs,
}: FormulaEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)
  const columnsRef = useRef(columns)
  columnsRef.current = columns
  const schemaRef = useRef(schema)
  schemaRef.current = schema
  // Read by the language/completion extensions added in later phases; kept here now so the
  // Compartment-reconfigure effect can be wired without touching this constructor again.
  const localeRef = useRef(locale)
  localeRef.current = locale
  const functionMetadataRef = useRef(functionMetadata)
  functionMetadataRef.current = functionMetadata
  const { t } = useI18n()
  const helpHide = t('grid.formulaHelp.hide')
  const helpShow = t('grid.formulaHelp.show')
  const helpLabelsRef = useRef<SignatureHelpLabels>({ hide: helpHide, show: helpShow })
  helpLabelsRef.current = { hide: helpHide, show: helpShow }
  const dockedHelp = variant === 'panel'

  // The extensions below are built once per mount; keeping the latest callbacks in refs lets
  // them always call current props without needing to rebuild the EditorState.
  const onChangeRef = useRef(onChange)
  const onCommitRef = useRef(onCommit)
  const onCancelRef = useRef(onCancel)
  const onActiveTypingChangeRef = useRef(onActiveTypingChange)
  const onFocusChangeRef = useRef(onFocusChange)
  onChangeRef.current = onChange
  onCommitRef.current = onCommit
  onCancelRef.current = onCancel
  onActiveTypingChangeRef.current = onActiveTypingChange
  onFocusChangeRef.current = onFocusChange
  const operandRefsRef = useRef(operandRefs)
  operandRefsRef.current = operandRefs
  const onPointRefRef = useRef(onPointRef)
  onPointRefRef.current = onPointRef
  const pointedRef = useRef<{ from: number; to: number; text: string } | null>(null)
  // F2 overrides what Left/Right do until the next keystroke: 'edit' moves the text cursor, 'point' moves the reference.
  const arrowModeRef = useRef<'edit' | 'point' | null>(null)

  const languageCompartment = useRef(new Compartment()).current
  const completionCompartment = useRef(new Compartment()).current
  const wrappingCompartment = useRef(new Compartment()).current

  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    const isCell = variant === 'cell'
    // A completion popup is open: let CM6's own `completionKeymap` handle Enter/Escape (accept the
    // suggestion / close the popup) instead of committing or canceling the whole formula.
    const commitKeymap = isCell
      ? [
          { key: 'Alt-Enter', run: insertNewlineAndIndent },
          { key: 'Enter', run: (view: EditorView) => (completionStatus(view.state) ? false : (onCommitRef.current(), view.contentDOM.blur(), true)) },
          { key: 'Tab', run: (view: EditorView) => (completionStatus(view.state) ? false : (onCommitRef.current(), view.contentDOM.blur(), true)) },
        ]
      : singleLine
      ? [
          { key: 'Alt-Enter', run: insertNewlineAndIndent },
          { key: 'Enter', run: (view: EditorView) => (completionStatus(view.state) ? false : (onCommitRef.current(), view.contentDOM.blur(), true)) },
        ]
      : [
          { key: 'Alt-Enter', run: insertNewlineAndIndent },
          { key: 'Mod-Enter', run: (view: EditorView) => (completionStatus(view.state) ? false : (onCommitRef.current(), true)) },
        ]

    // At the start, or after an operator or `(`, with only the end, a closing `)`, a separator or an
    // operator after the cursor — never before the leading `=` or touching a reference or a name.
    const awaitsOperand = (text: string, pos: number) =>
      /(^|[=(,;+\-*/^&:])\s*$/.test(text.slice(0, pos)) && /^\s*($|[)+\-*/^&,;:<>])/.test(text.slice(pos))

    const point = (deltaRow: number, deltaCol: number) => (view: EditorView) => {
      const resolve = onPointRefRef.current
      if (!resolve) return false
      const selection = view.state.selection.main
      if (!selection.empty) return false
      const pos = selection.head
      const text = view.state.doc.toString()
      const last = pointedRef.current
      const continuing = last !== null && last.to === pos && text.slice(last.from, last.to) === last.text
      // Up/Down belong to the suggestion list; they only move a pointer Left/Right or a click placed.
      if (deltaRow !== 0 && (!continuing || completionStatus(view.state))) return false
      const awaitingOperand = awaitsOperand(text, pos)
      // Left/Right point where a reference is expected or keep moving the one just pointed; touching any other
      // reference or a function name, they edit the text.
      const mode = arrowModeRef.current ?? (deltaCol !== 0 ? (continuing || awaitingOperand ? 'point' : 'edit') : null)
      if (mode === 'edit') return false
      if (!continuing && !awaitingOperand) return false
      const from = continuing ? last.from : pos
      const ref = resolve(deltaRow, deltaCol, !continuing)
      // Point mode outranks the autocomplete list that opens after an operator, as in Sheets.
      closeCompletion(view)
      if (ref === null) return true
      view.dispatch({ changes: { from, to: pos, insert: ref }, selection: { anchor: from + ref.length }, annotations: pointAnnotation.of(true) })
      pointedRef.current = { from, to: from + ref.length, text: ref }
      return true
    }

    // Ctrl+Space right after pointing at a cell turns `[Col]12` / `[@Col]` into the whole `[Col]`.
    const widenToColumn = (view: EditorView) => {
      const last = pointedRef.current
      const selection = view.state.selection.main
      if (!last || !selection.empty || selection.head !== last.to || view.state.sliceDoc(last.from, last.to) !== last.text) return false
      const match = /^(.*\[)@?((?:[^\]]|\]\])*\])\d*$/.exec(last.text)
      if (!match) return false
      const ref = (match[1] ?? '') + (match[2] ?? '')
      closeCompletion(view)
      view.dispatch({ changes: { from: last.from, to: last.to, insert: ref }, selection: { anchor: last.from + ref.length }, annotations: pointAnnotation.of(true) })
      pointedRef.current = { from: last.from, to: last.from + ref.length, text: ref }
      return true
    }

    // F2 flips Left/Right between moving the text cursor and pointing at cells, as in Excel.
    const toggleArrowMode = (view: EditorView) => {
      const pos = view.state.selection.main.head
      const text = view.state.doc.toString()
      const last = pointedRef.current
      const continuing = last !== null && last.to === pos && text.slice(last.from, last.to) === last.text
      const awaitingOperand = awaitsOperand(text, pos)
      const current = arrowModeRef.current ?? (continuing || awaitingOperand ? 'point' : 'edit')
      arrowModeRef.current = current === 'point' ? 'edit' : 'point'
      // Leaving point mode fixes the pointed reference; the next arrow or grid click starts a new one.
      if (arrowModeRef.current === 'edit') pointedRef.current = null
      // Pointing from a spot that takes no operand would splice a reference into the text; only a fresh pointer can move.
      if (arrowModeRef.current === 'point' && !continuing && !awaitingOperand) arrowModeRef.current = 'edit'
      return true
    }

    const extensions: Extension[] = [
      Prec.highest(
        keymap.of([
          { key: 'ArrowUp', run: point(-1, 0) },
          { key: 'ArrowDown', run: point(1, 0) },
          { key: 'ArrowLeft', run: point(0, -1) },
          { key: 'ArrowRight', run: point(0, 1) },
          { key: 'Ctrl-Space', mac: 'Cmd-Space', run: widenToColumn },
          { key: 'F2', run: toggleArrowMode },
        ]),
      ),
      Prec.highest(
        keymap.of([
          { key: 'Escape', run: (view) => (completionStatus(view.state) ? false : (onCancelRef.current(), singleLine ? view.contentDOM.blur() : undefined, true)) },
          ...commitKeymap,
        ]),
      ),
      history(),
      closeBrackets(),
      keymap.of([...closeBracketsKeymap, ...historyKeymap, ...defaultKeymap]),
      stripDotBeforeBracket,
      compositionFallback,
      // Order matters: CM6 computes StateFields in the order their extensions are added, and
      // formulaSignatureHelp's field reads the completion field's live state (currentCompletions)
      // to avoid overlapping the two tooltips. It must come after completionCompartment so that
      // read sees THIS transaction's completion result, not the previous one.
      wrappingCompartment.of(!singleLine || autoFocus || isCell ? EditorView.lineWrapping : []),
      completionCompartment.of(completionExtensions(schemaRef.current, functionMetadataRef.current, localeRef.current, () => operandRefsRef.current ?? [])),
      languageCompartment.of(languageExtensions(localeRef.current, functionMetadataRef.current, helpLabelsRef.current, dockedHelp)),
      placeholderExtension(placeholder),
      EditorView.contentAttributes.of({
        'data-formula-editor': '1',
        autocorrect: 'off',
        autocapitalize: 'off',
        spellcheck: 'false',
      }),
      formulaTheme(singleLine, variant),
      EditorView.updateListener.of((update) => {
        // A value pushed in from the `value` prop is not the user's edit; reporting it would mark
        // the owner dirty (e.g. the formula bar would show ✕/✓ after just selecting a column).
        if (update.docChanged && !update.transactions.some((tr) => tr.annotation(externalValueAnnotation))) {
          onChangeRef.current(update.state.doc.toString())
        }
        // Anything the user types ends the current pointing; the next arrow starts a new reference.
        if (update.docChanged && !update.transactions.some((tr) => tr.annotation(pointAnnotation))) {
          pointedRef.current = null
          arrowModeRef.current = null
        }
        // A click in the editor ends pointing too.
        if (update.transactions.some((tr) => tr.isUserEvent('select.pointer'))) {
          pointedRef.current = null
          arrowModeRef.current = null
        }
        if (update.docChanged || update.selectionSet) {
          const text = update.state.doc.toString()
          const pos = update.state.selection.main.head
          const typing = refBeingTyped(text, pos) !== null || funcBeingTyped(text, pos) !== null
          onActiveTypingChangeRef.current?.(typing)
        }
        if (update.focusChanged) {
          const isFocused = update.view.hasFocus
          onFocusChangeRef.current?.(isFocused)
          if (singleLine && !isCell) {
            update.view.dispatch({
              effects: wrappingCompartment.reconfigure(isFocused ? EditorView.lineWrapping : []),
            })
            if (!isFocused) {
              update.view.scrollDOM.scrollLeft = 0
            }
          }
        }
      }),
    ]

    const view = new EditorView({
      state: EditorState.create({
        doc: value,
        selection: { anchor: value.length },
        extensions,
      }),
      parent: node,
    })
    viewRef.current = view
    if (autoFocus) {
      view.focus()
      view.dispatch({ selection: { anchor: value.length } })
    }

    return () => {
      view.destroy()
      viewRef.current = null
    }
    // `value`/`onChange`/etc. are intentionally excluded: this effect only (re)creates the
    // EditorView, which must happen once per mount, not on every keystroke. Live prop changes are
    // applied imperatively by the effects below via `view.dispatch`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleLine, placeholder, languageCompartment, completionCompartment, wrappingCompartment])

  // Controlled value: only push an external change when the prop actually diverges from the
  // editor's own document, so the user's typing/selection is never fought.
  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    const current = view.state.doc.toString()
    if (current === value) return
    view.dispatch({
      changes: { from: 0, to: current.length, insert: value },
      selection: { anchor: value.length },
      annotations: externalValueAnnotation.of(true),
    })
  }, [value])

  // The decimal/argument separator is locale-dependent, and the signature tooltip's text is
  // UI-locale-dependent, so both are rebuilt whenever either changes, without remounting the editor.
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: languageCompartment.reconfigure(languageExtensions(locale, functionMetadata, { hide: helpHide, show: helpShow }, dockedHelp)),
    })
  }, [locale, functionMetadata, helpHide, helpShow, dockedHelp, languageCompartment])

  // What the completion source offers changes as the workbook's sheets and columns change, as a
  // lazily-loaded document list arrives, or as the UI locale changes.
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: completionCompartment.reconfigure(completionExtensions(schema, functionMetadata, locale, () => operandRefsRef.current ?? [])),
    })
  }, [schema, functionMetadata, locale, completionCompartment])

  useEffect(() => {
    if (autoFocus) viewRef.current?.focus()
  }, [autoFocus])

  useEffect(() => {
    viewRef.current?.dom.classList.toggle('formula-editor--error', syntaxError !== null)
  }, [syntaxError])

  // A header was clicked in the Grid: insert it, as Sheets does while a formula is open.
  const lastInsert = useRef<number | null>(null)
  useEffect(() => {
    if (insertRequest === undefined || insertRequest.nonce === lastInsert.current) return
    lastInsert.current = insertRequest.nonce
    const view = viewRef.current
    if (!view) return
    const name = columnsRef.current.find((c) => c.field === insertRequest.field)?.name ?? insertRequest.field
    const escaped = name.replace(/\s*\n\s*/g, ' ').trim().replace(/]/g, ']]')
    const pos = view.state.selection.main.head
    const text = view.state.doc.toString()
    const typing = refBeingTyped(text, pos)
    const from = typing ? typing.start : pos
    // Swallow a closing `]` sitting right after the caret, so the auto-closed pair does not leave
    // a stray bracket behind.
    const to = text[pos] === ']' ? pos + 1 : pos
    const insertText = `[${escaped}]`
    view.dispatch({
      changes: { from, to, insert: insertText },
      selection: { anchor: from + insertText.length },
    })
    view.focus()
  }, [insertRequest])

  const lastPoint = useRef<number | null>(null)
  useEffect(() => {
    if (pointRequest === undefined || pointRequest.nonce === lastPoint.current) return
    lastPoint.current = pointRequest.nonce
    const view = viewRef.current
    if (!view) return
    const selection = view.state.selection.main
    const text = view.state.doc.toString()
    const last = pointedRef.current
    const continuing = selection.empty && last !== null && last.to === selection.head && text.slice(last.from, last.to) === last.text
    const from = continuing ? last.from : selection.from
    const ref = pointRequest.ref
    closeCompletion(view)
    view.dispatch({ changes: { from, to: selection.to, insert: ref }, selection: { anchor: from + ref.length }, annotations: pointAnnotation.of(true) })
    pointedRef.current = { from, to: from + ref.length, text: ref }
    view.focus()
  }, [pointRequest])

  return <div ref={containerRef} className={singleLine ? 'min-w-0' : 'h-full min-w-0'} />
}
